import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Plus, FileText, Clock, User, ChevronRight, Search, Filter,
  FlaskConical, FileSpreadsheet, Receipt, Scale, CreditCard, AlertCircle,
  FileX, Mail, X, Loader2, Shield, FolderOpen, ClipboardCheck,
  Banknote, UserPlus, TrendingUp, Inbox, CalendarClock, Briefcase,
  Sun, ExternalLink, Trash2, PenTool, PoundSterling,
  ChevronDown, ChevronUp, Users, BookOpen, Home, ArrowRight, FolderInput, ExternalLink as ExternalLinkIcon
} from "lucide-react";
import { Input } from "../components/ui/input";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { InfoTooltip, ActionTooltip } from "../components/InfoTooltip";


import OnboardingStatusView from "../components/OnboardingStatusView";
// Brief 51.1 (14 Jul 2026) — HelpSidebar retired; Mate now sole helper.
// The former ``import HelpSidebar from "../components/HelpSidebar";`` line
// was deleted here as part of the Help→Mate merge. Do not re-add.
import CreditControlWidget from "../components/CreditControlWidget";
import PaymentRequestsWidget from "../components/PaymentRequestsWidget";
// Brief 51.1 (14 Jul 2026) — CommandPalette + BonsaiMate hoisted to
// ``AppLayout.jsx`` so they fire from any page, not only Dashboard.
// Their imports and useState hooks are gone from this file; see the
// listener cleanup below.
import TodayWithMate from "../components/TodayWithMate";
import { getUserPanels, getUserPracticeAreas, getPipelinePeers, getUserRole, isAssistantRole, PANELS, ROLES, getUserFeeEarner } from "../config/roleConfig";
import { requestSectionsFor } from "../config/requestSections"; // BM-H4986
import {
  MyMattersPanel,
  DeadlinesNudgesPanel,
  ResiChecklistsPanel,
  PostCompletionTasksPanel,
  PostIntakeQueuePanel,
  FileClosureRiskPanel,
  AccountsTodayPanel,
} from "../components/DashboardPanels";
import PipelineTrackerCore from "../components/PipelineTrackerCore";
import FirmMoneyBarCard from "../components/FirmMoneyBarCard";
import MyMoneyOnAccountPanel from "../components/MyMoneyOnAccountPanel";
import MyTrainingPanel from "../components/MyTrainingPanel"; // BM-H3630
import { clientDisplayName } from "../utils/clientDisplayName";

const CC_STATUS_COLORS = {
  clear: { bg: "bg-teal-dim/30", text: "text-teal", label: "Clear" },
  clear_with_conditions: { bg: "bg-status-amber-dim/30", text: "text-status-amber", label: "Conditional" },
  conflict: { bg: "bg-status-red-dim/30", text: "text-status-red", label: "Conflict" },
  not_ready: { bg: "bg-slate-800/50", text: "text-slate-400", label: "Not Ready" },
  na: { bg: "bg-blue-900/30", text: "text-blue-400", label: "N/A" },
  do_not_act: { bg: "bg-status-red-dim/50", text: "text-status-red", label: "Do Not Act" },
};

// Tiles = all action/request buttons. Everyone sees all sections except Jonathan
// (post scanning), who sees only the Pre-CaseMinder card ("pre_cm"): BM-H4986
// (Vanessa, 2 Nov 2026) "please ensure anyone can register pre-CM matters".
const ROLE_CARD_SECTIONS = {
  [ROLES.ADMIN]:          ["compliance", "accounts", "client"],
  [ROLES.DIRECTOR]:       ["compliance", "accounts", "client"],
  [ROLES.RESI_FE]:        ["compliance", "accounts", "client"],
  [ROLES.RESI_ASSISTANT]: ["compliance", "accounts", "client"],
  [ROLES.COMM_PROP]:      ["compliance", "accounts", "client"],
  [ROLES.COCO_LIT]:       ["compliance", "accounts", "client"],
  [ROLES.POST_SCANNING]:  ["pre_cm"],
  [ROLES.ACCOUNTS]:       ["compliance", "accounts", "client"],
};

function getCardSectionsForRole(email) {
  const role = getUserRole(email);
  if (!role) return ["compliance", "client"];
  return ROLE_CARD_SECTIONS[role] || ["compliance", "client"];
}

function ConflictCheckHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFlagged, setShowFlagged] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const userId = sessionStorage.getItem("bonsai_user_id");
        const resp = await axios.get(`${API}/conflict-checks/health`, { headers: { "X-Bonsai-User-Id": userId } });
        setData(resp.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="text-center py-8 text-txt-muted text-sm">Loading health data...</div>;
  if (!data) return <div className="text-center py-8 text-status-red text-sm">Failed to load health data</div>;

  const { summary, monthly_trends, fee_earner_breakdown, potentially_affected } = data;
  const sb = summary.status_breakdown || {};

  return (
    <div className="space-y-4" data-testid="cc-health-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total", value: summary.total, color: "text-white" },
          { label: "Clearance", value: `${summary.clearance_rate}%`, color: "text-teal" },
          { label: "Avg Resolution", value: summary.avg_resolution_hours ? `${summary.avg_resolution_hours}h` : "N/A", color: "text-blue-400" },
          { label: "Flagged", value: summary.potentially_affected_count, color: summary.potentially_affected_count > 0 ? "text-status-amber" : "text-teal" },
          { label: "Conflicts", value: sb.conflict || 0, color: (sb.conflict || 0) > 0 ? "text-status-red" : "text-teal" },
        ].map(s => (
          <div key={s.label} className="bg-navy-card rounded-md p-3 border border-navy-border">
            <p className="text-[10px] text-txt-muted uppercase tracking-wider">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>
      <div>
        <p className="text-xs text-txt-muted mb-1.5">Status Distribution</p>
        <div className="flex rounded-md overflow-hidden h-5">
          {Object.entries(sb).map(([status, count]) => {
            const pct = count / summary.total * 100;
            if (pct < 1) return null;
            const s = CC_STATUS_COLORS[status] || { bg: "bg-slate-700", text: "text-slate-300", label: status };
            return (
              <div key={status} className={`${s.bg} flex items-center justify-center relative group`} style={{ width: `${pct}%` }}>
                {pct > 10 && <span className={`text-[9px] font-medium ${s.text}`}>{s.label} ({count})</span>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-navy-card rounded-md p-4 border border-navy-border">
          <p className="text-xs text-txt-muted mb-2">Monthly Trends</p>
          <div className="space-y-1.5">
            {monthly_trends.map(m => {
              const max = Math.max(...monthly_trends.map(t => t.total), 1);
              return (
                <div key={m.month} className="flex items-center gap-2">
                  <span className="text-[10px] text-txt-muted w-14 shrink-0">{m.month}</span>
                  <div className="flex-1 flex h-4 rounded overflow-hidden bg-navy-hover">
                    <div className="bg-teal-dim/60" style={{ width: `${m.clear / max * 100}%` }} />
                    <div className="bg-status-red-dim/60" style={{ width: `${m.conflict / max * 100}%` }} />
                    <div className="bg-slate-700/60" style={{ width: `${m.other / max * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-txt-muted w-6 text-right">{m.total}</span>
                </div>
              );
            })}
            {monthly_trends.length === 0 && <p className="text-xs text-txt-faint">No data</p>}
          </div>
        </div>
        <div className="bg-navy-card rounded-md p-4 border border-navy-border">
          <p className="text-xs text-txt-muted mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> Decision Makers</p>
          <div className="space-y-1 max-h-[160px] overflow-y-auto">
            {fee_earner_breakdown.map(fe => (
              <div key={fe.name} className="flex items-center justify-between py-1 border-b border-navy-border last:border-0">
                <span className="text-xs text-txt truncate max-w-[120px]">{fe.name}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-teal">{fe.clear}</span>
                  {fe.conflict > 0 && <span className="text-[10px] text-status-red">{fe.conflict}</span>}
                  <span className="text-[10px] text-txt-muted font-medium w-5 text-right">{fe.total}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {potentially_affected.length > 0 && (
        <div className="bg-navy-card border border-amber-700/30 rounded-md">
          <button onClick={() => setShowFlagged(!showFlagged)} className="w-full flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-status-amber" />
              <span className="text-xs font-medium text-status-amber">{potentially_affected.length} Flagged for Review</span>
            </div>
            {showFlagged ? <ChevronUp className="w-3.5 h-3.5 text-txt-muted" /> : <ChevronDown className="w-3.5 h-3.5 text-txt-muted" />}
          </button>
          {showFlagged && (
            <div className="border-t border-amber-700/20 overflow-x-auto max-h-48">
              <table className="w-full text-xs">
                <thead><tr className="text-txt-muted border-b border-navy-border"><th className="text-left px-3 py-1.5">Client</th><th className="text-left px-3 py-1.5">Matter Ref</th><th className="text-left px-3 py-1.5">Decision Maker</th><th className="text-left px-3 py-1.5">Date</th></tr></thead>
                <tbody>
                  {potentially_affected.map((cc, i) => (
                    <tr key={i} className="border-b border-navy-border hover:bg-status-amber-dim/10">
                      <td className="px-3 py-1.5 text-txt">{clientDisplayName(cc)}</td>
                      <td className="px-3 py-1.5 text-txt-muted">{cc.matter_reference || cc.provisional_id}</td>
                      <td className="px-3 py-1.5 text-txt-muted">{cc.decision_maker_name}</td>
                      <td className="px-3 py-1.5 text-txt-muted">{cc.created_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MorningDigestCard() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDigest = async () => {
      try {
        const token = localStorage.getItem("token");
        const bonsaiUserId = localStorage.getItem("bonsai_user_id");
        if (!bonsaiUserId) { setLoading(false); return; }
        const res = await axios.get(`${API}/bonsai/emails/digest`, {
          headers: { Authorization: `Bearer ${token}`, "X-Bonsai-User-Id": bonsaiUserId }
        });
        if (res.data && res.data.total_inbox_7d > 0) setDigest(res.data);
      } catch { }
      setLoading(false);
    };
    fetchDigest();
  }, []);

  if (loading || !digest) return null;

  const stats = [
    { label: "Unfiled", value: digest.unfiled_count, color: "text-status-amber bg-status-amber-dim border-amber-200" },
    { label: "Client-related", value: digest.likely_client_related, color: "text-blue-600 bg-blue-50 border-blue-200" },
    { label: "Courts/Authorities", value: digest.courts_authorities, color: "text-status-red bg-status-red-dim border-red-200", hide: !digest.courts_authorities },
    { label: "Newsletters", value: digest.newsletters_low_priority, color: "text-gray-600 bg-gray-50 border-gray-200", hide: !digest.newsletters_low_priority },
  ].filter(s => !s.hide);

  return (
    <div className="mb-8 glass-card rounded-md p-5 animate-fade-in-up" data-testid="morning-digest-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sun className="w-5 h-5 text-status-amber" />
          <h3 className="text-sm font-semibold text-status-amber">Morning filing digest</h3>
          <span className="text-xs text-status-amber/70 ml-1">Last 7 days</span>
        </div>
        <span className="text-xs text-gray-500">{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" })}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {stats.map((s) => (
          <div key={s.label} className={`rounded-md border px-3 py-2 text-center ${s.color}`}>
            <div className="text-xl font-bold">{s.value}</div>
            <div className="text-xs">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Daily snapshot of unfiled emails. Metadata only — no subjects or bodies shown.</p>
        <Button variant="outline" size="sm" className="border-amber-300 text-status-amber hover:bg-status-amber-dim gap-1.5" onClick={() => window.open("https://bonsai-caseminder-portal.online/bonsai", "_blank")} data-testid="review-in-bonsai-btn">
          <ExternalLink className="w-3.5 h-3.5" />
          Review in Bonsai Mail
        </Button>
      </div>
    </div>
  );
}

export function FileMoverModal({ onClose }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [destSearch, setDestSearch] = useState("");
  const [destMatters, setDestMatters] = useState([]);
  const [selectedDest, setSelectedDest] = useState(null);
  const [destSubfolders, setDestSubfolders] = useState([]);
  const [selectedSubfolder, setSelectedSubfolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);

  const userId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id");
  const headers = { "X-Bonsai-User-Id": userId || "" };

  const searchFiles = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hub/file-mover/search`, { params: { q: searchQuery }, headers });
      setSearchResults(res.data.items || []);
    } catch (e) { toast.error("Search failed"); }
    setLoading(false);
  };

  const searchMatters = async () => {
    if (!destSearch.trim()) return;
    try {
      const res = await axios.get(`${API}/bonsai/filing/matters`, { params: { q: destSearch }, headers });
      setDestMatters(res.data.matters || res.data || []);
    } catch (e) { toast.error("Matter search failed"); }
  };

  const selectDest = async (matter) => {
    setSelectedDest(matter);
    setSelectedSubfolder(null);
    try {
      const res = await axios.get(`${API}/hub/file-mover/folder-children`, {
        params: { drive_id: matter.drive_id || matter.driveId, folder_id: matter.id || matter.itemId }, headers
      });
      setDestSubfolders((res.data.items || []).filter(i => i.is_folder));
    } catch { setDestSubfolders([]); }
  };

  const moveFile = async () => {
    if (!selectedFile || !selectedDest) return;
    const dest = selectedSubfolder || selectedDest;
    setMoving(true);
    try {
      const res = await axios.post(`${API}/hub/file-mover/move`, {
        source_drive_id: selectedFile.drive_id,
        source_item_id: selectedFile.id,
        destination_drive_id: selectedDest.drive_id || selectedDest.driveId,
        destination_folder_id: dest.id || dest.itemId,
      }, { headers });
      toast.success(`Moved '${res.data.file_name}' successfully`);
      onClose();
    } catch (e) { toast.error(errorMessage(e, "Move failed")); }
    setMoving(false);
  };

  const shortPath = (p) => {
    if (!p) return "";
    const parts = p.split("/");
    return parts.slice(-3).join(" / ");
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy-card border border-navy-border rounded-lg w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="file-mover-modal">
        <div className="flex items-center justify-between p-4 border-b border-navy-border">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FolderInput className="w-5 h-5 text-purple-400" />
            File Mover
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-txt-muted hover:text-white">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wider">1. Select Source File</h3>
              <div className="flex gap-2">
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search for file..." className="bg-navy-card border-navy-border text-white text-sm"
                  onKeyDown={e => e.key === "Enter" && searchFiles()} data-testid="file-mover-source-search" />
                <Button size="sm" onClick={searchFiles} disabled={loading} className="bg-purple-700 hover:bg-purple-600 text-white">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {searchResults.map((f, i) => (
                  <div key={f.id || i} onClick={() => setSelectedFile(f)}
                    className={`p-2 rounded text-xs cursor-pointer transition-colors border ${
                      selectedFile?.id === f.id ? "border-purple-500 bg-purple-900/30 text-white" : "border-transparent hover:bg-navy-hover text-txt-muted"
                    }`} data-testid={`source-file-${i}`}>
                    <p className="font-medium truncate">{f.name}</p>
                    <p className="text-[10px] opacity-60 truncate">{shortPath(f.parent_path)}</p>
                  </div>
                ))}
                {searchResults.length === 0 && searchQuery && !loading && (
                  <p className="text-xs text-txt-muted text-center py-4">No results</p>
                )}
              </div>
              {selectedFile && (
                <div className="p-2 rounded bg-purple-900/20 border border-purple-500/30 text-xs text-purple-300">
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="opacity-60 truncate">{shortPath(selectedFile.parent_path)}</p>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-teal uppercase tracking-wider">2. Select Destination</h3>
              <div className="flex gap-2">
                <Input value={destSearch} onChange={e => setDestSearch(e.target.value)}
                  placeholder="Search matter..." className="bg-navy-card border-navy-border text-white text-sm"
                  onKeyDown={e => e.key === "Enter" && searchMatters()} data-testid="file-mover-dest-search" />
                <Button size="sm" onClick={searchMatters} className="bg-teal hover:bg-teal text-white">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {destMatters.map((m, i) => (
                  <div key={m.id || m.itemId || i} onClick={() => selectDest(m)}
                    className={`p-2 rounded text-xs cursor-pointer transition-colors border ${
                      selectedDest?.id === m.id ? "border-teal bg-teal-dim/30 text-white" : "border-transparent hover:bg-navy-hover text-txt-muted"
                    }`} data-testid={`dest-matter-${i}`}>
                    <p className="font-medium">{m.name || m.title}</p>
                  </div>
                ))}
              </div>
              {destSubfolders.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-txt-muted">Subfolder:</p>
                  {destSubfolders.map((sf, i) => (
                    <div key={sf.id || i} onClick={() => setSelectedSubfolder(sf)}
                      className={`p-2 rounded text-xs cursor-pointer transition-colors border ${
                        selectedSubfolder?.id === sf.id ? "border-teal bg-teal-dim/30 text-white" : "border-transparent hover:bg-navy-hover text-txt-muted"
                      }`} data-testid={`dest-subfolder-${i}`}>
                      <FolderOpen className="w-3 h-3 inline mr-1.5 opacity-60" />{sf.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-navy-border flex items-center justify-between">
          <div className="text-xs text-txt-muted">
            {selectedFile && selectedDest ? (
              <span className="text-white">
                {selectedFile.name} <ArrowRight className="w-3 h-3 inline mx-1" /> {(selectedSubfolder || selectedDest)?.name}
              </span>
            ) : "Select a source file and destination"}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-txt-muted">Cancel</Button>
            <Button size="sm" onClick={moveFile} disabled={!selectedFile || !selectedDest || moving}
              className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="file-mover-move-btn">
              {moving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FolderInput className="w-4 h-4 mr-1.5" />}
              Move File
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MailboxAuditModal({ onClose }) {
  const [sentItems, setSentItems] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("sent");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [sentRes, receiptRes] = await Promise.all([
          axios.get(`${API}/email-audit/sent-items`, { params: { top: 50, search } }),
          axios.get(`${API}/email-audit/delivery-receipts`, { params: { top: 50 } }),
        ]);
        setSentItems(sentRes.data.items || sentRes.data || []);
        setReceipts(receiptRes.data.items || receiptRes.data || []);
      } catch (e) {
        toast.error("Failed to load mailbox data");
      }
      setLoading(false);
    })();
  }, [search]);

  const items = tab === "sent" ? sentItems : receipts;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy-card border border-navy-border rounded-lg w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="mailbox-audit-modal">
        <div className="flex items-center justify-between p-4 border-b border-navy-border">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Inbox className="w-5 h-5 text-status-amber" />
            no-reply@bonsai.law Mailbox
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-txt-muted hover:text-white" data-testid="close-mailbox-modal">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex gap-2 px-4 pt-3">
          <Button size="sm" variant={tab === "sent" ? "default" : "outline"} onClick={() => setTab("sent")} className={tab === "sent" ? "bg-status-amber text-white" : "text-status-amber border-amber-500/30"} data-testid="mailbox-tab-sent">
            Sent Items ({sentItems.length})
          </Button>
          <Button size="sm" variant={tab === "receipts" ? "default" : "outline"} onClick={() => setTab("receipts")} className={tab === "receipts" ? "bg-status-amber text-white" : "text-status-amber border-amber-500/30"} data-testid="mailbox-tab-receipts">
            Delivery Receipts ({receipts.length})
          </Button>
          {tab === "sent" && (
            <Input placeholder="Search sent items..." value={search} onChange={e => setSearch(e.target.value)} className="ml-auto w-48 h-8 bg-navy-hover border-navy-border text-sm text-white" data-testid="mailbox-search" />
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="text-center py-8 text-txt-muted"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-txt-muted">No items found</div>
          ) : items.map((item, i) => (
            <div key={i} className="bg-navy-hover/60 border border-navy-border rounded-md p-3 hover:bg-navy-hover transition-colors" data-testid={`mailbox-item-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{item.subject || "(No subject)"}</p>
                  <p className="text-xs text-txt-muted mt-0.5">
                    To: {item.toRecipients?.map(r => r.emailAddress?.address).join(", ") || item.to || "—"}
                  </p>
                </div>
                <span className="text-[10px] text-txt-muted whitespace-nowrap">
                  {item.sentDateTime ? format(parseISO(item.sentDateTime), "dd/MM/yy HH:mm") : item.receivedDateTime ? format(parseISO(item.receivedDateTime), "dd/MM/yy HH:mm") : ""}
                </span>
              </div>
              {item.bodyPreview && <p className="text-xs text-txt-muted mt-1 line-clamp-2">{item.bodyPreview}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Resolve an axios / fetch error into a single user-facing string.
// Prefers window.errorMessage(err, fallback) when the host page provides
// it (parity with the addin error helper) — never stringifies an Error
// directly, never falls back silently to console.error.
function _sofErr(err, fallback) {
  return errorMessage(err, fallback);
}

// Admin-only view of SoF questionnaire link sends (PR #434 follow-up).
// Reads /api/email-audit/sof-sends and renders a date / matter-ref /
// client / fee-earner table with a day-range selector and optional
// fee-earner filter. No silent fallbacks — every error surfaces via
// _sofErr() into a toast and a visible error band in the modal.
export function SofSendsModal({ onClose }) {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [days, setDays] = useState(7);
  const [feeEarnerFilter, setFeeEarnerFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = { days };
        if (feeEarnerFilter.trim()) {
          params.fee_earner_email = feeEarnerFilter.trim().toLowerCase();
        }
        const res = await axios.get(`${API}/email-audit/sof-sends`, { params });
        if (cancelled) return;
        setSends(res.data?.sends || []);
      } catch (err) {
        if (cancelled) return;
        const msg = _sofErr(err, "Failed to load SoF sends");
        setError(msg);
        toast.error(msg);
        setSends([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, feeEarnerFilter]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy-card border border-navy-border rounded-lg w-full max-w-4xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="sof-sends-modal">
        <div className="flex items-center justify-between p-4 border-b border-navy-border">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-teal" />
            SoF Questionnaire Sends
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-txt-muted hover:text-white" data-testid="close-sof-sends-modal">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 px-4 pt-3">
          {[7, 30, 90].map(n => (
            <Button
              key={n}
              size="sm"
              variant={days === n ? "default" : "outline"}
              onClick={() => setDays(n)}
              className={days === n ? "bg-teal text-white" : "text-teal border-teal/30"}
              data-testid={`sof-sends-days-${n}`}
            >
              {n} days
            </Button>
          ))}
          <Input
            placeholder="Filter by fee earner email…"
            value={feeEarnerFilter}
            onChange={e => setFeeEarnerFilter(e.target.value)}
            className="ml-auto w-64 h-8 bg-navy-hover border-navy-border text-sm text-white"
            data-testid="sof-sends-fe-filter"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-3 px-3 py-2 rounded-md bg-status-red-dim/20 border border-status-red/40 text-sm text-status-red" data-testid="sof-sends-error">
              {error}
            </div>
          )}
          {loading ? (
            <div className="text-center py-8 text-txt-muted" data-testid="sof-sends-loading">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Loading…
            </div>
          ) : sends.length === 0 ? (
            <div className="text-center py-8 text-txt-muted" data-testid="sof-sends-empty">
              No SoF questionnaire sends in this window.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="sof-sends-table">
              <thead className="text-xs uppercase text-txt-muted border-b border-navy-border">
                <tr>
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Matter Ref</th>
                  <th className="text-left py-2 px-2">Client Email</th>
                  <th className="text-left py-2 px-2">Fee Earner</th>
                </tr>
              </thead>
              <tbody>
                {sends.map((row, i) => {
                  let when = row.created_at;
                  try {
                    when = format(parseISO(row.created_at), "dd/MM/yy HH:mm");
                  } catch (_e) {
                    // Leave raw string if it can't be parsed — don't hide it.
                  }
                  const ref = row.matter_reference || "";
                  return (
                    <tr key={i} className="border-b border-navy-border/40 hover:bg-navy-hover/40" data-testid={`sof-sends-row-${i}`}>
                      <td className="py-2 px-2 text-txt-muted whitespace-nowrap">{when}</td>
                      <td className="py-2 px-2">
                        {ref ? (
                          <Link
                            to={`/forf/${encodeURIComponent(ref)}`}
                            className="text-teal hover:underline"
                            data-testid={`sof-sends-matter-link-${i}`}
                          >
                            {ref}
                          </Link>
                        ) : (
                          <span className="text-txt-muted">—</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-white">{row.client_email || "—"}</td>
                      <td className="py-2 px-2 text-white">
                        {row.fee_earner_name || row.fee_earner_email || "—"}
                        {row.fee_earner_name && row.fee_earner_email && (
                          <span className="block text-[10px] text-txt-muted">{row.fee_earner_email}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// Director's Lounge modal — wraps the Director Tools content
// New Request cards. Each card has its own hue pair; green, amber and red
// are kept out because they are status colours. Brief 11c: Completion Funds
// soft-deleted from quick-actions (Payment Request covers it).
const REQUEST_SECTIONS = [
  {
    key: "compliance", label: "Compliance & Golden Rule", icon: Scale, hue: ["#818cf8", "#c084fc"], cols: "lg:grid-cols-4",
    cards: [
      { title: "New Matter / Conflict Search", desc: "Start every new matter here", href: "/conflict-search", icon: Shield, testid: "new-conflicts-search-card", hue: ["#6366f1", "#a855f7"] },
      { title: "FORF & Risk Assessment", desc: "New matter file opening & CCL", href: "/conflict-search", icon: Scale, testid: "new-matter-card", hue: ["#8b5cf6", "#d946ef"] },
      { title: "Pre-CaseMinder", desc: "Existing matters for CCL & conflicts", href: "/pre-caseminder", icon: FolderOpen, testid: "pre-caseminder-card", hue: ["#3b82f6", "#6366f1"] },
      { title: "Risk Assessment", desc: "Mid-point or standalone review", href: "/risk-assessment", icon: Shield, testid: "risk-assessment-card", hue: ["#0ea5e9", "#3b82f6"] },
    ],
  },
  {
    key: "accounts", label: "Accounts & Finance", icon: PoundSterling, hue: ["#f472b6", "#c084fc"], cols: "lg:grid-cols-3",
    cards: [
      { title: "Invoice Request", desc: "Request invoice from Accounts", href: "/invoice-request", icon: Receipt, testid: "new-invoice-card", hue: ["#ec4899", "#a855f7"] },
      { title: "Payment Request", desc: "Single payment from Accounts", href: "/payment-request", icon: CreditCard, testid: "new-payment-card", hue: ["#d946ef", "#6366f1"] },
      { title: "Third-Party Invoices", desc: "Upload invoices for May to process", href: "/third-party-invoices", icon: FileSpreadsheet, testid: "third-party-invoices-card", hue: ["#a855f7", "#ec4899"] },
    ],
  },
  {
    key: "client", label: "Client & Matter", icon: User, hue: ["#22d3ee", "#60a5fa"], cols: "lg:grid-cols-4",
    cards: [
      { title: "Send SoF Questionnaire", desc: "Email the full Source of Funds questionnaire", href: "/send-sof-questionnaire", icon: Mail, testid: "send-sof-questionnaire-card", hue: ["#06b6d4", "#3b82f6"] },
      { title: "File Closure", desc: "Risk checks & notify client care", href: "/file-closure", icon: FileX, testid: "new-file-closure-card", hue: ["#0ea5e9", "#8b5cf6"] },
    ],
  },
];

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [formTypeFilter, setFormTypeFilter] = useState("all");
  const [staffFilter, setStaffFilter] = useState("all");
  
  const tabFromUrl = searchParams.get("tab") || "submissions";
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [conflictSubView, setConflictSubView] = useState("checks");
  
  const statusFromUrl = searchParams.get("status") || null;
  const feeEarnerFromUrl = searchParams.get("fee_earner") || null;
  const highlightId = searchParams.get("highlight") || null;
  
  const navigate = useNavigate();
  
  const [aiQuery, setAiQuery] = useState("");
  const [aiSearching, setAiSearching] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [aiExplanation, setAiExplanation] = useState("");

  const [postStats, setPostStats] = useState(null);
  const [urgentDeadlines, setUrgentDeadlines] = useState([]);
  // Brief 51.1 (14 Jul 2026) — ``helpOpen`` deleted (Help retired).

  const [isDirector, setIsDirector] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Brief 51.1 (14 Jul 2026) — ``cmdOpen`` + ``mateOpen`` state hoisted
  // to ``AppLayout.jsx``. ``helpOpen`` deleted entirely (Help retired).

  // Brief 51.1 (14 Jul 2026) — cmd/ctrl-K keyboard shortcut hoisted to
  // ``AppLayout.jsx`` so the palette fires from every page. Deleted
  // here — do not re-add.

  // BM-H626 (26 Sep 2026): the Director Tools pop-up is now the Tools tab of
  // the Directors Lounge (/directors). Old /?dl=1 links go there.
  useEffect(() => {
    if (searchParams.get("dl") === "1") {
      navigate("/directors?tab=tools", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount
  }, []);

  const userStr = localStorage.getItem("user");
  const userObj = userStr ? JSON.parse(userStr) : null;
  const userEmail = (
    userObj?.user_email ||
    userObj?.email ||
    userObj?.ms_email ||
    localStorage.getItem("bonsai_user_email") ||
    sessionStorage.getItem("bonsai_user_email") ||
    ""
  ).toLowerCase().trim();
  const userRole = getUserRole(userEmail);
  const userPanels = getUserPanels(userEmail);
  const userPracticeAreas = getUserPracticeAreas(userEmail);
  const isAdmin = userRole === ROLES.ADMIN;
  const isAssistant = isAssistantRole(userEmail);
  const canViewAllMatters = isAdmin || userRole === ROLES.DIRECTOR;
  const isAccountsRole = userRole === ROLES.ACCOUNTS;

  const visibleCardSections = getCardSectionsForRole(userEmail);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams, activeTab]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const params = new URLSearchParams(searchParams);
    if (newTab === "submissions") {
      params.delete("tab");
      params.delete("status");
      params.delete("fee_earner");
    } else {
      params.set("tab", newTab);
    }
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    fetchSubmissions();
    fetchPostIntakeStats();
    checkDirectorAccess();
  }, []);

  const checkDirectorAccess = async () => {
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      if (!userId) return;
      const resp = await axios.get(`${API}/user-role`, { headers: { "X-Bonsai-User-Id": userId } });
      setIsDirector(resp.data?.is_director === true);
    } catch { }
  };

  const handleDeleteSubmission = async () => {
    if (!deleteReason.trim() || !deleteTargetId) { toast.error("A deletion reason is required"); return; }
    setDeleting(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      await axios.delete(`${API}/submissions/${deleteTargetId}`, {
        data: { reason: deleteReason.trim() },
        headers: { "X-Bonsai-User-Id": userId }
      });
      toast.success("Submission deleted successfully");
      setShowDeleteModal(false);
      setDeleteReason("");
      setDeleteTargetId(null);
      fetchSubmissions();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete submission"));
    } finally {
      setDeleting(false);
    }
  };

  const fetchPostIntakeStats = async () => {
    try {
      const [statsRes, deadlinesRes] = await Promise.all([
        axios.get(`${API}/postintake/stats`),
        axios.get(`${API}/postintake/deadlines/untriaged?limit=5`),
      ]);
      setPostStats(statsRes.data);
      setUrgentDeadlines(deadlinesRes.data.deadlines || []);
    } catch (err) {
      console.error("Post intake stats fetch failed:", err);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const [subRes, ccRes] = await Promise.all([
        axios.get(`${API}/submissions`),
        axios.get(`${API}/conflict-checks/recent?limit=100`)
      ]);
      const conflictChecks = (ccRes.data.conflict_checks || [])
        .filter(cc => !cc.is_test && !cc.searched_name?.startsWith('TEST_') && !cc.client_name?.startsWith('TEST_'))
        .map(cc => ({
          id: cc.id,
          form_type: "conflict_check",
          primary_label: clientDisplayName(cc) || cc.searched_name || "Unknown",
          secondary_label: cc.conflict_status === "cleared" ? `Cleared — ${cc.matter_reference || cc.provisional_id || "No ID"}` : `Status: ${cc.conflict_status || "pending"}`,
          tertiary_label: cc.searched_by_name || cc.decision_maker_name || "",
          status: cc.conflict_status || "pending",
          created_at: cc.created_at,
          matter_reference: cc.matter_reference || cc.provisional_id,
          submission_id: cc.submission_id,
          has_forf: cc.has_forf,
        }));
      setSubmissions([...subRes.data, ...conflictChecks]);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const staffMembers = [...new Set(submissions.map(sub => {
    if (sub.form_type === "invoice_request" || sub.form_type === "payment_request") return sub.requester_name || "";
    return sub.tertiary_label || "";
  }).filter(Boolean))];

  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = !searchTerm || 
      sub.primary_label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.secondary_label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.matter_reference?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = formTypeFilter === "all" || sub.form_type === formTypeFilter;
    const staffLabel = (sub.form_type === "invoice_request" || sub.form_type === "payment_request")
      ? sub.requester_name || ""
      : sub.tertiary_label || "";
    const matchesStaff = staffFilter === "all" || staffLabel === staffFilter;
    return matchesSearch && matchesType && matchesStaff;
  });

  const getStatusBadge = (status, formType) => {
    if (formType === "conflict_check") {
      const s = CC_STATUS_COLORS[status] || CC_STATUS_COLORS.not_ready;
      return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.bg} ${s.text}`}>{s.label}</span>;
    }
    const colors = {
      pending: "bg-status-amber-dim/30 text-status-amber",
      approved: "bg-teal-dim/30 text-teal",
      rejected: "bg-status-red-dim/30 text-status-red",
      completed: "bg-blue-900/30 text-blue-300",
    };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${colors[status] || "bg-slate-800 text-slate-300"}`}>{status}</span>;
  };

  const getFormIcon = (formType) => {
    const icons = {
      invoice_request: Receipt,
      payment_request: CreditCard,
      conflict_check: Shield,
      new_matter: Scale,
      file_closure: FileX,
      risk_assessment: AlertCircle,
      completion_statement: FileText,
      accounts_instruction: Banknote,
      pre_exchange: ClipboardCheck,
      pre_completion: ClipboardCheck,
      post_completion: ClipboardCheck,
    };
    const Icon = icons[formType] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  const handleAiSearch = async () => {
    if (!aiQuery.trim()) return;
    setAiSearching(true);
    setAiResults(null);
    try {
      const res = await axios.post(`${API}/submissions/ai-search`, { query: aiQuery });
      setAiResults(res.data.results || []);
      setAiExplanation(res.data.explanation || "");
    } catch {
      toast.error("AI search failed");
    }
    setAiSearching(false);
  };

  const showPipelineMini = userPanels.includes(PANELS.PIPELINE_MINI);
  const showMyMatters = userPanels.includes(PANELS.MY_MATTERS);
  const showDeadlinesNudges = userPanels.includes(PANELS.DEADLINES_NUDGES);
  const showResiChecklists = userPanels.includes(PANELS.RESI_CHECKLISTS);
  const showPostCompletionTasks = userPanels.includes(PANELS.POST_COMPLETION_TASKS);
  const showPostIntakeQueue = userPanels.includes(PANELS.POST_INTAKE_QUEUE);
  const showFileClosureRisk = userPanels.includes(PANELS.FILE_CLOSURE_RISK);
  const showAccountsToday = userPanels.includes(PANELS.ACCOUNTS_TODAY);

  return (
    <div className="min-h-screen bg-navy" data-testid="dashboard-container">
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6" data-testid="dashboard-main">

        {/* Brief 50.1: mounts above TodayWithMate. Internal role guard
            (26 Sep 2026: getUserFeeEarner + ROLES.ACCOUNTS) restricts this
            to the accounts role, so no role check is needed here. */}
        <PaymentRequestsWidget />

        <TodayWithMate onOpenMate={() => window.dispatchEvent(new CustomEvent("open-bonsai-mate"))} />

        {/* Role-specific panels */}
        {showPipelineMini && (() => {
          const peers = getPipelinePeers(userEmail);
          const practiceAreas = getUserPracticeAreas(userEmail);
          return (
            <PipelineTrackerCore
              lockedFeeEarners={peers || []}
              lockedPracticeAreas={practiceAreas}
              compact
              showMoneyBar
              showSummaryBar={false}
              maxHeight="700px"
            />
          );
        })()}
        {isAccountsRole && <FirmMoneyBarCard />}
        {/* BM-H116 — fee earners who don't already see the firm-wide
            FirmMoneyBarCard (Directors/Accounts/Admin) get their own
            MOA panel, scoped to their own active matters only. */}
        <MyTrainingPanel />
        {!canViewAllMatters && !isAccountsRole && <MyMoneyOnAccountPanel />}
        {showAccountsToday && <AccountsTodayPanel userEmail={userEmail} />}
        {showMyMatters && <MyMattersPanel practiceAreas={userPracticeAreas} />}
        {showDeadlinesNudges && <DeadlinesNudgesPanel />}
        {showResiChecklists && <ResiChecklistsPanel />}
        {showPostCompletionTasks && <PostCompletionTasksPanel />}
        {showPostIntakeQueue && <PostIntakeQueuePanel />}
        {showFileClosureRisk && <FileClosureRiskPanel />}

        {isAccountsRole && (
          <div className="mb-6">
            <OnboardingStatusView
              defaultStatusFilter={statusFromUrl}
              defaultFeeEarnerFilter={feeEarnerFromUrl}
              highlightId={highlightId}
            />
          </div>
        )}

        {isAccountsRole && <CreditControlWidget />}
        {isAdmin && <MorningDigestCard />}

        {/* New Request Cards — each card glows in its own hue (cm-card). */}
        {visibleCardSections.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-white mb-5">New Request</h2>
            {requestSectionsFor(REQUEST_SECTIONS, visibleCardSections).map(sec => (
              <React.Fragment key={sec.key}>
                <p className="text-xs font-bold uppercase tracking-wider mb-2.5 flex items-center gap-1.5" style={{ "--c1": sec.hue[0], "--c2": sec.hue[1] }}>
                  <sec.icon className="w-3.5 h-3.5" style={{ color: sec.hue[0] }} />
                  <span className="cm-label">{sec.label}</span>
                </p>
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${sec.cols} gap-3 mb-6`}>
                  {sec.cards.map(card => (
                    <ActionTooltip key={card.testid} title={card.title} description={card.desc}>
                      <button
                        onClick={() => navigate(card.href)}
                        className="cm-card group relative overflow-hidden w-full bg-navy-card border border-white/[0.07] p-4 rounded-xl text-left flex items-center gap-3.5"
                        style={{ "--c1": card.hue[0], "--c2": card.hue[1] }}
                        data-testid={card.testid}
                      >
                        <span aria-hidden className="absolute inset-0 opacity-50 group-hover:opacity-100 transition-opacity duration-300"
                          style={{ background: `radial-gradient(120% 100% at 0% 0%, ${card.hue[0]}2e, transparent 60%)` }} />
                        <span className="cm-tile relative w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ring-1 ring-white/20">
                          <card.icon className="w-[18px] h-[18px] text-white" />
                        </span>
                        <div className="relative min-w-0">
                          <span className="text-sm font-bold text-white block truncate">{card.title}</span>
                          <span className="text-xs text-txt-muted block truncate mt-0.5">{card.desc}</span>
                        </div>
                      </button>
                    </ActionTooltip>
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

      </main>

      {/* Modals */}
      {/* Brief 51.1 (14 Jul 2026) — CommandPalette + BonsaiMate hoisted
          to ``AppLayout.jsx`` so they fire from any page. HelpSidebar
          deleted entirely — Mate is now the sole helper. */}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-navy-card border border-red-700/40 rounded-lg p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold mb-2">Delete Submission</h3>
            <p className="text-sm text-txt-muted mb-3">This action is logged and irreversible. State the reason:</p>
            <Input value={deleteReason} onChange={e => setDeleteReason(e.target.value)} placeholder="Reason for deletion..." className="bg-navy-card border-navy-border text-white mb-4" />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteModal(false)} className="text-txt-muted">Cancel</Button>
              <Button size="sm" onClick={handleDeleteSubmission} disabled={deleting || !deleteReason.trim()} className="bg-status-red hover:bg-status-red text-white">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
